import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNodeById } from "../../../abstract/controller/nodeSelector";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { setHighlight } from "../../../abstract/redux/slice/overlaySlice";
import { getIdFunction, getRoot } from "../../../abstract/redux/slice/structureSlice";
import IconBxsDownArrow from "../../../themes/icons/IconBxsDownArrow";
import IconBxsRightArrow from "../../../themes/icons/IconBxsRightArrow";
import IconDash from "../../../themes/icons/IconDash";
import DefaultTooltip, * as vendorTooltip from "../../ui/tooltips/DefaultTooltip";
import styles from "./AsideElementNavigation.module.scss";

function AsideElementNavigation({
  name,
  showGlobals,
  setShowGlobals,
  showFunction,
  setShowFunction,
  showConditions,
  setShowConditions,
}) {
  const _root = useSelector(getRoot);
  const _idFunction = useSelector(getIdFunction);
  const { data: _node } = getNodeById(_root, _idFunction);

  return (
    <div className={styles["navigation"]}>
      <SectionFunction node={_node} showFunction={showFunction} setShowFunction={setShowFunction} />
      <SectionConditions
        node={_node}
        showConditions={showConditions}
        setShowConditions={setShowConditions}
      />
    </div>
  );
}

const SectionFunction = ({ node, showFunction, setShowFunction }) => {
  if (!setShowFunction) {
    return null;
  }

  const _parametersLocal = nodeUtils.getParameters(node);

  return (
    <Container
      text={`(${_parametersLocal.length}) parameters`}
      length={_parametersLocal.length}
      initial>
      <div className={styles["navigation__container__list"]}>
        {_parametersLocal.map((e) => (
          <ContainerParameters
            key={nodeUtils.getID(e)}
            node={e}
            isSectionVisible={showFunction}
            setSectionVisible={setShowFunction}
          />
        ))}
      </div>
    </Container>
  );
};

const SectionConditions = ({ node, showConditions, setShowConditions }) => {
  if (!setShowConditions) {
    return null;
  }

  const _conditionsAliases = nodeUtils.getConditionsAliases(node);
  const _conditionsConstraints = nodeUtils.getConditionsConstraints(node);
  const _conditionsAssignments = nodeUtils.getConditionsAssignments(node);
  const _conditions = [..._conditionsConstraints, ..._conditionsAssignments, ..._conditionsAliases];

  return (
    <Container text={`(${_conditions.length}) logic`} length={_conditions.length}>
      <div className={styles["navigation__container__list"]}>
        <ContainerConditions
          node={node}
          isSectionVisible={showConditions}
          setSectionVisible={setShowConditions}
        />
      </div>
    </Container>
  );
};

const Container = ({ text, initial, length, children }) => {
  const [_showNavigation, _setShowNavigation] = useState(initial ?? false);

  const handleOnClick = () => {
    _setShowNavigation((e) => !e);
  };

  return (
    <>
      <div className={styles["navigation__container"]} role="button" onClick={handleOnClick}>
        <span className={styles["navigation__container__icon"]}>
          {_showNavigation ? <IconBxsDownArrow /> : <IconBxsRightArrow />}
        </span>
        &nbsp;
        <span className={styles["navigation__container__header"]}>{text}</span>
      </div>
      <div
        className={`${styles[`navigation__container__content`]} ${
          !_showNavigation || !length ? styles["styles--collapse"] : ""
        }`}>
        {children}
      </div>
    </>
  );
};

const ContainerParameters = ({ isSectionVisible, setSectionVisible, node }) => {
  const dispatch = useDispatch();
  const [_showElement, _setShowElement] = useState(false);
  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _hasParameters = nodeUtils.hasParameters(node);

  const handleOnClick = () => {
    setSectionVisible(true);
    dispatch(setHighlight(_id));
  };

  const handleOnClickItem = () => {
    _setShowElement((e) => !e);
  };

  return (
    <>
      <div onClick={handleOnClick}>
        <div
          className={styles["navigation__container__list__item"]}
          data-tooltip-id={_uuid}
          data-tooltip-delay-show={vendorTooltip.getTimeSlow()}
          onClick={handleOnClickItem}>
          <span className={styles["navigation__container__icon"]}>
            {_hasParameters ? (
              _showElement ? (
                <IconBxsDownArrow />
              ) : (
                <IconBxsRightArrow />
              )
            ) : (
              <IconDash />
            )}
          </span>
          &nbsp;
          <div className={styles["text-box"]}>{nodeUtils.getName(node)}</div>
        </div>
      </div>

      <DefaultTooltip handle={_uuid}>
        <TooltipParameter node={node} />
      </DefaultTooltip>

      {_hasParameters && _showElement && (
        <ul className={styles["navigation__container__list"]}>
          {nodeUtils.getParameters(node).map((e) => (
            <ContainerParameters
              key={nodeUtils.getID(e)}
              node={e}
              isSectionVisible={isSectionVisible}
              setSectionVisible={setSectionVisible}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const ContainerConditions = ({ setSectionVisible, node }) => {
  const dispatch = useDispatch();

  const handleOnClick = (id) => {
    setSectionVisible(true);
    dispatch(setHighlight(id));
  };

  return (
    <>
      <ContainerConditionsAlias node={node} handleOnClick={handleOnClick} />
      <ContainerConditionsConstraint node={node} handleOnClick={handleOnClick} />
      <ContainerConditionsAssignment node={node} handleOnClick={handleOnClick} />
    </>
  );
};

const ContainerConditionsAlias = ({ node, handleOnClick }) => {
  const [_showAlias, _setShowAlias] = useState(false);

  const _aliases = nodeUtils.getConditionsAliases(node);

  const handleShowAlias = () => {
    _setShowAlias((e) => !e);
  };

  return (
    <>
      <div onClick={handleShowAlias}>
        <div className={styles["navigation__container__list__item"]}>
          <span className={styles["navigation__container__icon"]}>
            {_showAlias ? <IconBxsDownArrow /> : <IconBxsRightArrow />}
          </span>
          &nbsp;({_aliases.length})&nbsp;Alias
        </div>
      </div>
      {_showAlias && _aliases.length > 0 && (
        <div className={`${styles["navigation__container__list"]} ${styles["styles--padding"]}`}>
          {_aliases.map((e) => (
            <div key={`${nodeUtils.getID(e)}`}>
              <div
                className={styles["navigation__container__list__item"]}
                data-tooltip-id={nodeUtils.getUUID(e)}
                data-tooltip-delay-show={vendorTooltip.getTimeSlow()}
                onClick={() => handleOnClick(nodeUtils.getID(e))}>
                <span className={styles["navigation__container__icon"]}>
                  <IconDash />
                </span>
                &nbsp;
                <div className={styles["text-box"]}>{nodeUtils.getName(e)}</div>
              </div>
              <DefaultTooltip handle={nodeUtils.getUUID(e)}>
                <TooltipCondition node={e} />
              </DefaultTooltip>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const ContainerConditionsConstraint = ({ node, handleOnClick }) => {
  const [_showConstraint, _setShowConstraint] = useState(false);

  const _constraints = nodeUtils.getConditionsConstraints(node);

  const handleConstraint = () => {
    _setShowConstraint((e) => !e);
  };

  return (
    <>
      <div onClick={handleConstraint}>
        <div className={styles["navigation__container__list__item"]}>
          <span className={styles["navigation__container__icon"]}>
            {_showConstraint ? <IconBxsDownArrow /> : <IconBxsRightArrow />}
          </span>
          &nbsp;({_constraints.length}) Constraint
        </div>
      </div>
      {_showConstraint && _constraints.length > 0 && (
        <div className={`${styles["navigation__container__list"]} ${styles["styles--padding"]}`}>
          {_constraints.map((e) => (
            <div key={`${nodeUtils.getID(e)}`}>
              <div
                className={styles["navigation__container__list__item"]}
                data-tooltip-id={nodeUtils.getUUID(e)}
                data-tooltip-delay-show={vendorTooltip.getTimeSlow()}
                onClick={() => handleOnClick(nodeUtils.getID(e))}>
                <span className={styles["navigation__container__icon"]}>
                  <IconDash />
                </span>
                &nbsp;
                <div className={styles["text-box"]}>{nodeUtils.getName(e)}</div>
              </div>

              <DefaultTooltip handle={nodeUtils.getUUID(e)}>
                <TooltipCondition node={e} />
              </DefaultTooltip>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const ContainerConditionsAssignment = ({ node, handleOnClick }) => {
  const [_showAssignment, _setShowAssignment] = useState(false);

  const _assignments = nodeUtils.getConditionsAssignments(node);

  const handleShowAssignment = () => {
    _setShowAssignment((e) => !e);
  };

  return (
    <>
      <div onClick={handleShowAssignment}>
        <div className={styles["navigation__container__list__item"]}>
          <span className={styles["navigation__container__icon"]}>
            {_showAssignment ? <IconBxsDownArrow /> : <IconBxsRightArrow />}
          </span>
          &nbsp;({_assignments.length}) Assignment
        </div>
      </div>
      {_showAssignment && _assignments.length > 0 && (
        <div className={`${styles["navigation__container__list"]} ${styles["styles--padding"]}`}>
          {_assignments.map((e) => (
            <div key={`${nodeUtils.getUUID(e)}`}>
              <div
                className={styles["navigation__container__list__item"]}
                data-tooltip-id={`aside-navigation-assignment-${nodeUtils.getID(e)}`}
                data-tooltip-delay-show={vendorTooltip.getTimeSlow()}
                onClick={() => handleOnClick(nodeUtils.getID(e))}>
                <span className={styles["navigation__container__icon"]}>
                  <IconDash />
                </span>
                &nbsp;{nodeUtils.getName(e)}
              </div>
              <DefaultTooltip handle={nodeUtils.getUUID(e)}>
                <TooltipCondition node={e} />
              </DefaultTooltip>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const TooltipParameter = ({ node }) => {
  const _target = nodeUtils.getIdTarget(node);
  const _output = nodeUtils.getValueDefault(node);

  return (
    <>
      <div>{nodeUtils.getName(node)}</div>
      {_target && (
        <>
          <div className={styles["tooltip__parameter"]}>Linked parameter name:</div>
          <div>{_target}</div>
        </>
      )}
      {_output && (
        <>
          <div className={styles["tooltip__parameter"]}>Output value:</div>
          <div>{`'${_output}'`}</div>
        </>
      )}
      {!_target && !_output && (
        <>
          <div className={styles["tooltip__parameter"]}>Parameter choices:</div>
          <div>
            {nodeUtils.getChoices(node).map((e) => (
              <TooltipChoice key={nodeUtils.getID(e)} node={e} />
            ))}
          </div>
        </>
      )}
    </>
  );
};

const TooltipChoice = ({ node }) => {
  return (
    <>
      <div className={styles["tooltip__choice__item"]}>{`- ${nodeUtils.getName(node)}`}</div>

      {nodeUtils.hasChoices(node) && (
        <ul className={styles["tooltip__choice"]}>
          {nodeUtils.getChoices(node).map((e) => (
            <TooltipChoice key={nodeUtils.getID(e)} node={e} />
          ))}
        </ul>
      )}
    </>
  );
};

const TooltipCondition = ({ node }) => {
  return (
    <>
      <div>{nodeUtils.getName(node)}</div>
      <br />
      <div>
        Expression: <br />- {nodeUtils.getExpression(node)}
      </div>
    </>
  );
};

export default AsideElementNavigation;
